import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgOverheadPeriodBucketRow } from "@/lib/manufacturing/overheadTypes";
import { OverheadPeriodDebtEdit } from "./OverheadPeriodDebtEdit";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;

function formatUsd(value: number): string {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function filled(row: MfgOverheadPeriodBucketRow): boolean {
  return row.assumed_value_usd != null || row.actual_value_usd != null;
}

function valueLabel(row: MfgOverheadPeriodBucketRow): string {
  const parts: string[] = [];
  if (row.assumed_value_usd != null) parts.push(`${formatUsd(row.assumed_value_usd)} assumed`);
  if (row.actual_value_usd != null) parts.push(`${formatUsd(row.actual_value_usd)} actual`);
  if (row.remaining_balance_usd != null) parts.push(`${formatUsd(row.remaining_balance_usd)} left`);
  return parts.join(" · ");
}

export function OverheadPeriodValueEditor({
  row,
  state,
  bare = false,
}: {
  row: MfgOverheadPeriodBucketRow;
  state: PeriodValuesState;
  bare?: boolean;
}) {
  const { summary, drafts, setDraft, actionBusy, saveAssumed, saveActual, reloadSummary } = state;
  const [open, setOpen] = useState(bare || !filled(row));
  if (!summary || row.is_aggregate) return null;
  const showing = bare || open;
  const tag = row.category.trim() || row.bucket_key;
  return (
    <div className="min-w-0 rounded-lg border border-gray-100 px-3 py-2 dark:border-zinc-800">
      {bare ? null : (
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={showing}
          onClick={() => setOpen((current) => !current)}
        >
          <span aria-hidden="true" className="text-gray-500 dark:text-zinc-400">{showing ? "▾" : "▸"}</span>
          <span className="min-w-0 truncate font-medium text-gray-900 dark:text-zinc-100">{row.bucket_name}</span>
          <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
            {tag}
          </span>
          {!showing ? <span className="ml-auto shrink-0 text-xs text-gray-600 dark:text-zinc-300">{valueLabel(row)}</span> : null}
        </button>
      )}
      {showing ? (
        <>
          <div className="mt-2 grid min-w-0 gap-2">
            <label className="block min-w-0 text-xs text-gray-600 dark:text-zinc-400">
              Assumed USD
              <input
                type="number"
                min={0}
                step="0.01"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={drafts[row.bucket_id]?.assumed ?? ""}
                onChange={(event) => setDraft(row.bucket_id, { assumed: event.target.value })}
                disabled={actionBusy || summary.period_state === "closed"}
              />
            </label>
            <label className="block min-w-0 text-xs text-gray-600 dark:text-zinc-400">
              Actual USD
              <input
                type="number"
                min={0}
                step="0.01"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                value={drafts[row.bucket_id]?.actual ?? ""}
                onChange={(event) => setDraft(row.bucket_id, { actual: event.target.value })}
                disabled={actionBusy || summary.period_state === "closing" || summary.period_state === "closed"}
              />
            </label>
          </div>
          <label className="mt-2 block text-xs text-gray-600 dark:text-zinc-400">
            Assumed reason
            <input
              type="text"
              placeholder="Required to save assumed"
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              value={drafts[row.bucket_id]?.reason ?? ""}
              onChange={(event) => setDraft(row.bucket_id, { reason: event.target.value })}
              disabled={actionBusy || summary.period_state === "closed"}
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} disabled={actionBusy || summary.period_state === "closed"} onClick={() => void saveAssumed(row)}>
              Save assumed
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={actionBusy || summary.period_state === "closing" || summary.period_state === "closed"}
              onClick={() => void saveActual(row)}
            >
              Book actual
            </button>
          </div>
          {row.tracks_balance ? (
            <OverheadPeriodDebtEdit
              bucketId={row.bucket_id}
              totalUsd={row.balance_start_usd ?? null}
              disabled={actionBusy}
              onSaved={reloadSummary}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
